/**
 * Report export utilities for CSV and PDF generation
 */

import type { Asset } from '@/types/models';
import { formatDate, formatCurrency } from './utils';

/**
 * Convert assets data to CSV format
 */
export function exportToCSV(assets: Asset[]): void {
  if (assets.length === 0) {
    console.warn('No assets to export');
    return;
  }

  // Define CSV headers
  const headers = [
    'Tag ID',
    'Description',
    'Brand',
    'Model',
    'Serial Number',
    'Status',
    'Category',
    'Department',
    'Location',
    'Cost',
    'Purchase Date',
    'Purchased From',
    'Warranty (months)',
    'Assigned To',
    'Created At',
    'Updated At',
  ];

  // Convert assets to CSV rows
  const rows = assets.map((asset) => [
    asset.tag_id || '',
    asset.description || '',
    asset.brand || '',
    asset.model || '',
    asset.serial_number || '',
    asset.status || '',
    asset.category?.name || '',
    asset.department?.name || '',
    asset.location?.name || '',
    asset.cost?.toString() || '',
    asset.purchase_date || '',
    asset.purchased_from || '',
    asset.warranty_period?.toString() || '',
    asset.assigned_to?.map((p) => p.full_name).join('; ') || '',
    asset.created_at || '',
    asset.updated_at || '',
  ]);

  // Escape CSV values (handle commas, quotes, newlines)
  const escapeCsvValue = (value: string): string => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  // Build CSV content
  const csvContent = [
    headers.map(escapeCsvValue).join(','),
    ...rows.map((row) => row.map(escapeCsvValue).join(',')),
  ].join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `assets-report-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Export assets data to PDF format
 * Uses browser print functionality with custom styling
 */
export function exportToPDF(assets: Asset[]): void {
  if (assets.length === 0) {
    console.warn('No assets to export');
    return;
  }

  // Create a new window for PDF generation
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    console.error('Failed to open print window. Please allow popups for this site.');
    return;
  }

  // Calculate summary statistics
  const totalValue = assets.reduce((sum, asset) => sum + (asset.cost || 0), 0);
  const statusCounts = assets.reduce((acc, asset) => {
    acc[asset.status] = (acc[asset.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Build HTML for PDF
  const htmlContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>Assets Report - ${new Date().toLocaleDateString()}</title>
        <style>
          @page {
            margin: 0.5in;
          }

          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 10pt;
            line-height: 1.4;
            color: #333;
          }

          .header {
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid #333;
          }

          h1 {
            margin: 0 0 5px 0;
            font-size: 20pt;
            color: #000;
          }

          .report-date {
            color: #666;
            font-size: 9pt;
          }

          .summary {
            background: #f5f5f5;
            padding: 15px;
            margin-bottom: 20px;
            border-radius: 5px;
          }

          .summary-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
          }

          .summary-item {
            text-align: center;
          }

          .summary-label {
            font-size: 9pt;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .summary-value {
            font-size: 16pt;
            font-weight: bold;
            color: #000;
            margin-top: 5px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }

          thead {
            background: #333;
            color: white;
          }

          th {
            padding: 8px 6px;
            text-align: left;
            font-weight: 600;
            font-size: 9pt;
          }

          td {
            padding: 6px;
            border-bottom: 1px solid #ddd;
            font-size: 9pt;
          }

          tbody tr:nth-child(even) {
            background: #f9f9f9;
          }

          .status-badge {
            display: inline-block;
            padding: 2px 8px;
            border-radius: 3px;
            font-size: 8pt;
            font-weight: 600;
          }

          .status-available { background: #d4edda; color: #155724; }
          .status-assigned { background: #d1ecf1; color: #0c5460; }
          .status-maintenance { background: #fff3cd; color: #856404; }
          .status-retired { background: #e2e3e5; color: #383d41; }
          .status-lost { background: #f8d7da; color: #721c24; }

          .footer {
            margin-top: 30px;
            padding-top: 10px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 8pt;
            color: #666;
          }

          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Assets Report</h1>
          <div class="report-date">Generated on ${new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}</div>
        </div>

        <div class="summary">
          <div class="summary-grid">
            <div class="summary-item">
              <div class="summary-label">Total Assets</div>
              <div class="summary-value">${assets.length}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Total Value</div>
              <div class="summary-value">${formatCurrency(totalValue)}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Average Value</div>
              <div class="summary-value">${formatCurrency(totalValue / assets.length)}</div>
            </div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Tag ID</th>
              <th>Description</th>
              <th>Brand</th>
              <th>Model</th>
              <th>Serial Number</th>
              <th>Status</th>
              <th>Category</th>
              <th>Cost</th>
              <th>Purchase Date</th>
            </tr>
          </thead>
          <tbody>
            ${assets.map((asset) => `
              <tr>
                <td>${asset.tag_id || '-'}</td>
                <td>${asset.description || '-'}</td>
                <td>${asset.brand || '-'}</td>
                <td>${asset.model || '-'}</td>
                <td>${asset.serial_number || '-'}</td>
                <td>
                  <span class="status-badge status-${asset.status.toLowerCase()}">
                    ${asset.status}
                  </span>
                </td>
                <td>${asset.category?.name || '-'}</td>
                <td>${formatCurrency(asset.cost)}</td>
                <td>${formatDate(asset.purchase_date)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          <p>This report contains ${assets.length} asset${assets.length !== 1 ? 's' : ''}</p>
        </div>

        <script>
          // Auto-print when page loads
          window.onload = function() {
            window.print();
            // Close window after printing (optional)
            // setTimeout(() => window.close(), 500);
          };
        </script>
      </body>
    </html>
  `;

  // Write content to print window
  printWindow.document.write(htmlContent);
  printWindow.document.close();
}

/**
 * Export summary statistics to CSV
 */
export function exportSummaryToCSV(assets: Asset[]): void {
  if (assets.length === 0) {
    console.warn('No assets to export');
    return;
  }

  // Calculate statistics
  const totalValue = assets.reduce((sum, asset) => sum + (asset.cost || 0), 0);
  const avgValue = totalValue / assets.length;

  const statusCounts = assets.reduce((acc, asset) => {
    acc[asset.status] = (acc[asset.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const categoryCounts = assets.reduce((acc, asset) => {
    const category = asset.category?.name || 'Uncategorized';
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Build CSV content
  const csvContent = [
    'Summary Report',
    `Generated: ${new Date().toISOString()}`,
    '',
    'Overall Statistics',
    `Total Assets,${assets.length}`,
    `Total Value,${totalValue}`,
    `Average Value,${avgValue.toFixed(2)}`,
    '',
    'By Status',
    ...Object.entries(statusCounts).map(([status, count]) => `${status},${count}`),
    '',
    'By Category',
    ...Object.entries(categoryCounts).map(([category, count]) => `${category},${count}`),
  ].join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `assets-summary-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}
